import { useRef } from "react";
import { Loader2, BellPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import CompleteProfileDialog from "@/components/CompleteProfileDialog";
import { ChatBubble, ChatComposer, ChatThread, ChatTyping } from "@/components/chat";
import ConciergeListingsRail from "@/components/concierge/ConciergeListingsRail";
import ConciergeListingSheet from "@/components/concierge/ConciergeListingSheet";
import type { AuthIntent, ConciergeConversationState } from "@/components/concierge/useConciergeConversation";
import { TEAM_INBOX_PATH } from "@/components/concierge/useConciergeConversation";
import { useTenant } from "@/hooks/useTenant";
import { whatsappDigits } from "@/lib/countryDialCodes";
import { track } from "@/lib/track";
import type { TranscriptItem } from "@/lib/conciergeTranscript";

// The conversation itself, thread, composer, listing sheet and the gates,
// without any surrounding chrome. The home widget wraps it in its full-screen
// Dialog (mobile: one column; desktop: chat column beside the listings panel),
// the inbox thread wraps it in its pane header. Bubbles speak the same design
// language as the human threads in /messages (ThreadView).

export type ConversationVariant = "overlay-mobile" | "overlay-desktop" | "inbox";

interface Props {
  conv: ConciergeConversationState;
  variant: ConversationVariant;
  /** Focus the composer on mount (the home overlay opens right after a send). */
  autoFocus?: boolean;
  className?: string;
}

const teamRedirect = encodeURIComponent(TEAM_INBOX_PATH);

const AUTH_COPY: Record<
  AuthIntent,
  { title: string; description: string; signupTo?: string; loginTo?: string; signupLabel?: string }
> = {
  request: {
    title: "Sign in to send your request",
    description: "Create a free account or log in to send your enquiry to the landlord. Your chat stays right here.",
  },
  save: {
    title: "Save your search & get alerts",
    description:
      "Create a free account and we'll email you the moment a home matching your chat is listed. Your preferences are already filled in.",
    signupTo: "/welcome",
    signupLabel: "Create account & save",
  },
  team: {
    title: "Sign in to talk to the team",
    description: "Create a free account or log in and we'll open a direct line to the team in your Messages.",
    signupTo: `/login?mode=signup&redirect=${teamRedirect}`,
    loginTo: `/login?mode=signin&redirect=${teamRedirect}`,
  },
};

const Bubble = ({
  msg,
  inlineCards,
  phoneHref,
  onOpenDetail,
  onRetry,
  onReset,
  onPhoneClick,
  onSendProposed,
  onDismissProposed,
  confirming,
}: {
  msg: TranscriptItem;
  onSendProposed: (item: TranscriptItem) => void;
  onDismissProposed: (item: TranscriptItem) => void;
  confirming: boolean;
  inlineCards: boolean;
  phoneHref: (raw: string) => string | null;
  onOpenDetail: (slug: string) => void;
  onRetry: (item: TranscriptItem) => void;
  onReset: () => void;
  onPhoneClick: () => void;
}) => {
  if (msg.kind === "confirm_contact" && msg.pending) {
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-[85%] rounded-2xl border bg-card p-3 space-y-2 text-sm">
          <p className="font-medium">{msg.text}</p>
          <p className="rounded-lg bg-muted px-3 py-2 whitespace-pre-wrap break-words">{msg.pending.message}</p>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full h-8" disabled={confirming} onClick={() => onSendProposed(msg)}>
              {confirming ? "Sending…" : "Send request"}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full h-8" disabled={confirming} onClick={() => onDismissProposed(msg)}>
              Not now
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (msg.role === "system") {
    return (
      <div className="flex flex-col items-center gap-2 py-1 text-center">
        <p className="text-xs text-muted-foreground">{msg.text}</p>
        {msg.kind === "session_full" && (
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={onReset}>
            Start a new chat
          </Button>
        )}
      </div>
    );
  }
  const failed = msg.kind === "failed" || msg.kind === "error";
  // The chat kit (src/components/chat): the renter is "mine", the team keeps
  // its tint and name, the assistant's text is shown as plain chat text.
  return (
    <ChatBubble
      role={msg.role === "user" ? "mine" : msg.role === "agent" ? "team" : "ai"}
      name={msg.role === "agent" ? "Team" : null}
      text={msg.text}
      at={msg.at}
      phoneHref={phoneHref}
      onLinkClick={(p) => p.kind === "phone" && onPhoneClick()}
      failed={failed ? { note: msg.note ?? "Couldn't send.", onRetry: msg.kind === "failed" ? () => onRetry(msg) : undefined } : null}
    >
      {/* On the desktop two-pane the cards live in the right panel, not inline. */}
      {inlineCards && msg.cards && msg.cards.length > 0 && (
        <div className="max-w-[92%] w-full">
          <ConciergeListingsRail cards={msg.cards} onOpenDetail={onOpenDetail} />
        </div>
      )}
    </ChatBubble>
  );
};

const ConciergeConversation = ({ conv, variant, autoFocus = false, className }: Props) => {
  const tenant = useTenant();
  const lastIntent = useRef<AuthIntent>("request");
  const overlay = variant !== "inbox";
  const inlineCards = variant !== "overlay-desktop";
  const dialogZ = overlay ? "z-[110]" : undefined;
  const locked = conv.maintenanceDisabled || conv.sessionFull;

  if (conv.authIntent) lastIntent.current = conv.authIntent;
  const authCopy = AUTH_COPY[lastIntent.current];


  const phoneHref = (raw: string) => {
    const digits = whatsappDigits(raw, tenant.country);
    return digits ? `https://wa.me/${digits}` : null;
  };

  return (
    <div className={cn("flex flex-col min-h-0", className)}>
      <ChatThread
        label="Chat with the concierge"
        scrollKey={`${conv.items.length}:${conv.sending}`}
        footer={
          <>
            {conv.restoring && (
              <div className="flex justify-center py-2" role="status" aria-label="Loading the conversation">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {conv.sending && <ChatTyping label="The concierge is writing" />}
          </>
        }
      >
        {conv.items.map((m) => (
          <Bubble
            key={m.id}
            msg={m}
            inlineCards={inlineCards}
            phoneHref={phoneHref}
            onOpenDetail={conv.openDetail}
            onRetry={conv.retry}
            onReset={conv.reset}
            onPhoneClick={() => track("whatsapp_click", { surface: "concierge" })}
            onSendProposed={conv.sendProposed}
            onDismissProposed={conv.dismissProposed}
            confirming={conv.confirming}
          />
        ))}
      </ChatThread>

      <ChatComposer
        label="Message to the concierge"
        value={conv.draft}
        onChange={conv.setDraft}
        onSend={() => void conv.submit()}
        sending={conv.sending}
        disabled={locked}
        placeholder={
          conv.maintenanceDisabled
            ? "Paused for maintenance"
            : conv.sessionFull
              ? "Start a new chat to continue"
              : "What are you looking for?"
        }
        safeArea={overlay}
        autoFocus={autoFocus}
        accessory={
          !conv.handoffActive && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="rounded-full shrink-0 h-11 w-11"
              onClick={conv.savePrefs}
              disabled={conv.sending || locked || !conv.canSavePrefs}
              title="Save preferences & get alerts"
              aria-label="Save preferences & get alerts"
            >
              <BellPlus className="h-4 w-4" />
            </Button>
          )
        }
      />

      <ConciergeListingSheet
        slug={conv.detailSlug}
        onOpenChange={(o) => !o && conv.closeDetail()}
        onAsk={conv.askAbout}
        elevated={overlay}
      />

      {/* Gates, raised above the z-[100] chat overlay on the home page. */}
      <CompleteProfileDialog
        open={conv.profileFix.open}
        onOpenChange={conv.profileFix.setOpen}
        missing={conv.profileFix.missing}
        initialValues={conv.profileFix.initial}
        submitLabel="Save & continue"
        overlayClassName={dialogZ}
        contentClassName={dialogZ}
        onCompleted={conv.profileFix.onCompleted}
      />

      <AuthPromptDialog
        open={conv.authIntent != null}
        onOpenChange={(o) => !o && conv.closeAuth()}
        title={authCopy.title}
        description={authCopy.description}
        signupTo={authCopy.signupTo}
        loginTo={authCopy.loginTo}
        signupLabel={authCopy.signupLabel}
        overlayClassName={dialogZ}
        contentClassName={dialogZ}
      />
    </div>
  );
};

export default ConciergeConversation;
